import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslation from './locales/en/translation.json';
import trTranslation from './locales/tr/translation.json';

export const supportedLanguages = ['en', 'tr'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const defaultLanguage: SupportedLanguage = 'en';
export const fallbackLanguage: SupportedLanguage = 'en';

export const resources = {
  tr: { translation: trTranslation },
  en: { translation: enTranslation },
};

const languageDetector = new LanguageDetector(null);

// Final detection step: when nothing was saved, fall back to the app's
// default language ('en') instead of sniffing the browser locale.
languageDetector.addDetector({
  name: 'appDefault',
  lookup: () => defaultLanguage,
  cacheUserLanguage: () => undefined,
});

const i18n = i18next.createInstance();

i18n.use(languageDetector).use(initReactI18next).init({
  resources,
  supportedLngs: [...supportedLanguages],
  nonExplicitSupportedLngs: true,
  fallbackLng: fallbackLanguage,
  detection: {
    // The suite's language plane mirrors the resolved value into localStorage
    // (suite-theme.js, before this module runs), so it leads; the legacy
    // NEXT_LOCALE cookie stays as a fallback below for pre-suite boots.
    order: ['localStorage', 'cookie', 'appDefault'],
    lookupCookie: 'NEXT_LOCALE',
    caches: ['cookie'],
    cookieMinutes: 60 * 24 * 365,
    cookieOptions: { path: '/', sameSite: 'lax' },
  },
  // Resources are bundled inline: initialize synchronously so the first render
  // (server-side included) already has translations.
  initImmediate: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

/**
 * The suite speaks ONE language: hand every language change in this document
 * — the top bar's menu, this app's own switchers and settings, the echo of a
 * suite broadcast — to the shared runtime, so the pick lands on the suite's
 * language plane and every application follows. The runtime ignores an echo
 * of its own resolved value, so the round trip stays loop-free.
 */
type SuiteLanguageRuntime = {
  language?: string;
  setLanguage?: (code: string) => void;
};

const rawChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (lng, callback) => {
  const runtime = (window as unknown as { SuiteTheme?: SuiteLanguageRuntime })
    .SuiteTheme;
  if (lng && runtime && typeof runtime.setLanguage === 'function') {
    runtime.setLanguage(lng);
  }
  return rawChangeLanguage(lng, callback);
};

export default i18n;
